"use client";

import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { XIcon } from "@/components/ui/XIcon";
import { useMediaQuery } from "@/hooks/use-media-query";

interface AddressChangeDialogProps {
  trigger?: ReactNode;
  children?: ReactNode;
}

export const AddressChangeDialog = ({
  trigger,
  children,
}: AddressChangeDialogProps) => {
  const t = useTranslations("modals");
  const TITLE = t("addressChange.title");
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const defaultTrigger = <Button>{t("addressChange.triggerButton")}</Button>;
  const dialogTrigger = trigger ?? defaultTrigger;

  // Desktop: Dialog
  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{dialogTrigger}</DialogTrigger>
        <DialogContent className="max-w-md space-y-0 py-4 px-8">
          <DialogHeader>
            <DialogTitle className="mb-4 flex justify-between items-center text-lg">
              {TITLE}
              <DialogClose asChild>
                <Button size={"icon"} variant={"light"}>
                  <XIcon size={16} />
                </Button>
              </DialogClose>
            </DialogTitle>
            <DialogDescription asChild>
              <div className="w-full">{children}</div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  // Mobile: Drawer
  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>{dialogTrigger}</DrawerTrigger>
      <DrawerContent className="p-8">
        <DrawerHeader className="flex-col items-start gap-2 px-0 pb-0">
          <DrawerTitle className="flex w-full justify-between items-center text-lg">
            {TITLE}
          </DrawerTitle>
          <DrawerDescription asChild className="p-0">
            <div className="w-full">{children}</div>
          </DrawerDescription>
        </DrawerHeader>
      </DrawerContent>
    </Drawer>
  );
};
